import { useDispatch, useSelector } from "react-redux";

import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { useTableSelections } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useTableSelections";
import {
    selectFilteredCompetitorsDomains,
    selectSelectedCompetitorsDomains,
} from "app/pages/sales-intelligence/modules/company-analysis/store/competitors/selectors";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import { selectExcelQuotaRemaining } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import { LeadsType } from "app/pages/sales-intelligence/sub-modules/company-search/types/common";
import LegacyWebsitesCRMExportPanelView from "app/pages/sales-intelligence/sub-modules/management/components/LegacyWebsitesCRMExportPanel/LegacyWebsitesCRMExportPanelView";
import WebsitesCRMExportPanelView from "app/pages/sales-intelligence/sub-modules/management/components/WebsitesCRMExportPanel/WebsitesCRMExportPanelView";
import { exportDomainsToDynamicsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/domainExport";
import { selectExportingToDynamics } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamics/exportSelectors";
import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/components/BaseActivatedPanel/types";
import type { SelectionDropdownItem } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/models/selection-dropdown-item/types";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import CrmExportSelectionDropdown from "./CrmExportSelectionDropdown";

const DynamicsCompetitorsExportPanel = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();
    const excelQuotaRemaining = useSelector(selectExcelQuotaRemaining);
    const selectedCompetitorsDomains = useSelector(selectSelectedCompetitorsDomains);
    const filteredCompetitorsDomains = useSelector(selectFilteredCompetitorsDomains);

    const isExportInProgress = useSelector(selectExportingToDynamics);

    const { clearAndCloseRowsSelection, onSelectionChange } = useTableSelections();

    const handleDeactivate = () => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    const handleSubmit = async (item: Readonly<SelectionDropdownItem>) => {
        trackingService.trackMultiSelectorExportClick("dynamics", excelQuotaRemaining, item.alias, item.value);

        const dto =
            item.id === ManualSelectionItem.ID
                ? selectedCompetitorsDomains
                : filteredCompetitorsDomains.slice(0, item.value);
        if (dto.length === 0) {
            return;
        }
        await dispatch(exportDomainsToDynamicsThunk(dto, [WORLDWIDE_COUNTRY_ID], LeadsType.Websites));
        handleDeactivate();
    };

    if (salesSettingsHelper.isDataCreditsEnabled) {
        return (
            <WebsitesCRMExportPanelView
                isDisabled={isExportInProgress}
                isLoading={isExportInProgress}
                label={{ key: "si.multi_selector.dynamics.title" }}
                numberOfSelectedItems={selectedCompetitorsDomains.length}
                numberOfRemainingExports={excelQuotaRemaining}
                numberOfResults={filteredCompetitorsDomains.length}
                selectionDropdown={CrmExportSelectionDropdown}
                onDeactivate={handleDeactivate}
                onSelectionChange={onSelectionChange}
                onSubmit={handleSubmit}
                exportType={ExportCostTypeItem.Dynamics}
            />
        );
    }

    return (
        <LegacyWebsitesCRMExportPanelView
            isDisabled={isExportInProgress}
            isLoading={isExportInProgress}
            label={{ key: "si.multi_selector.dynamics.title" }}
            numberOfSelectedItems={selectedCompetitorsDomains.length}
            numberOfRemainingExports={excelQuotaRemaining}
            numberOfResults={filteredCompetitorsDomains.length}
            selectionDropdown={CrmExportSelectionDropdown}
            onDeactivate={handleDeactivate}
            onSelectionChange={onSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default DynamicsCompetitorsExportPanel;
